import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import './AdminApplicationDetailPage.css';

const STATUSES = ['New','Under Review','Shortlisted','Discovery Meeting Scheduled','Selected','Not Selected','Converted to Paid Lead','Closed'];
const SCORE_CRITERIA = [
  { key: 'business_impact_score',       label: 'Business Impact',              max: 25 },
  { key: 'problem_clarity_score',       label: 'Clear Problem Statement',      max: 20 },
  { key: 'automation_potential_score',  label: 'Automation Potential',         max: 20 },
  { key: 'growth_potential_score',      label: 'Growth Potential',             max: 15 },
  { key: 'commercial_opportunity_score','label': 'Future Commercial Opportunity', max: 10 },
  { key: 'story_value_score',           label: 'Marketing / Story Value',      max: 10 },
];

const BADGE_MAP = {
  'New':'new','Under Review':'review','Shortlisted':'shortlisted','Selected':'selected',
  'Not Selected':'rejected','Discovery Meeting Scheduled':'meeting',
  'Converted to Paid Lead':'converted','Closed':'closed',
};

function DetailRow({ label, value }) {
  if (!value || (Array.isArray(value) && !value.length)) return null;
  return (
    <div className="detail-row">
      <div className="detail-row__label">{label}</div>
      <div className="detail-row__value">{Array.isArray(value) ? value.join(', ') : String(value)}</div>
    </div>
  );
}

export default function AdminApplicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState({});
  const [savingScore, setSavingScore] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [savingComment, setSavingComment] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getApplication(id);
      setDetail(data);
      setComments(data.comments || []);
      if (data.scores) {
        const s = {};
        SCORE_CRITERIA.forEach(c => { s[c.key] = data.scores[c.key] || 0; });
        setScores(s);
      } else {
        const s = {};
        SCORE_CRITERIA.forEach(c => { s[c.key] = 0; });
        setScores(s);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const totalScore = Object.values(scores).reduce((a, b) => a + (parseInt(b) || 0), 0);

  const handleScoreChange = (key, val) => {
    const max = SCORE_CRITERIA.find(c => c.key === key)?.max || 25;
    setScores(s => ({ ...s, [key]: Math.min(max, Math.max(0, parseInt(val) || 0)) }));
  };

  const saveScore = async () => {
    setSavingScore(true);
    try {
      await adminApi.saveScore(id, scores);
      await load();
      alert(`Score saved: ${totalScore}/100`);
    } catch { alert('Failed to save score.'); }
    finally { setSavingScore(false); }
  };

  const updateStatus = async (status) => {
    await adminApi.updateStatus(id, status);
    await load();
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setSavingComment(true);
    try {
      await adminApi.addComment(id, comment);
      setComment('');
      await load();
    } catch { alert('Failed to add comment.'); }
    finally { setSavingComment(false); }
  };

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;
  if (!detail) return <div className="page-loading">Application not found.</div>;

  const { application: app } = detail;

  return (
    <div className="detail-page">
      {/* Header */}
      <div className="detail-page__header">
        <button className="btn btn--ghost" onClick={() => navigate('/admin/applications')} style={{ padding: '6px 10px', fontSize: 13 }}>← Back</button>
        <div>
          <h1 style={{ fontSize: 20 }}>{app.company_name}</h1>
          <span className={`badge badge--${BADGE_MAP[app.status] || 'new'}`}>{app.status}</span>
        </div>
      </div>

      <div className="detail-grid">
        {/* Left column */}
        <div className="detail-left">
          {/* Status */}
          <div className="card mb-2">
            <h3 className="card-title">Update Status</h3>
            <select value={app.status} onChange={e => updateStatus(e.target.value)}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn btn--primary" style={{ flex: 1, fontSize: 13 }} onClick={() => updateStatus('Selected')}>✓ Select</button>
              <button className="btn btn--danger" style={{ flex: 1, fontSize: 13 }} onClick={() => updateStatus('Not Selected')}>✗ Reject</button>
            </div>
          </div>

          {/* Company info */}
          <div className="card mb-2">
            <h3 className="card-title">Company Information</h3>
            <DetailRow label="Company" value={app.company_name} />
            <DetailRow label="Industry" value={app.industry} />
            <DetailRow label="Contact" value={app.contact_person} />
            <DetailRow label="Designation" value={app.designation} />
            <DetailRow label="Mobile" value={app.mobile_number} />
            <DetailRow label="Email" value={app.email} />
            <DetailRow label="District" value={app.district} />
            <DetailRow label="Employees" value={app.employee_count} />
            <DetailRow label="Website" value={app.website_url} />
            <DetailRow label="Reg. Number" value={app.business_registration_number} />
          </div>

          {/* Consent */}
          <div className="card mb-2">
            <h3 className="card-title">Preferences & Consent</h3>
            <DetailRow label="Future Investment" value={app.future_investment_interest} />
            <DetailRow label="Publicity Consent" value={app.publicity_consent ? 'Yes' : 'No'} />
            <DetailRow label="Wants Consultation" value={app.consultation_interest ? 'Yes' : 'No'} />
            <DetailRow label="Service Interests" value={app.service_interests} />
          </div>

          {/* Scoring */}
          <div className="card">
            <h3 className="card-title">Scoring Panel</h3>
            {SCORE_CRITERIA.map(c => (
              <div key={c.key} className="score-row">
                <label>{c.label}</label>
                <div className="score-row__input">
                  <input type="number" min={0} max={c.max} value={scores[c.key] || 0}
                    onChange={e => handleScoreChange(c.key, e.target.value)} />
                  <span>/ {c.max}</span>
                </div>
              </div>
            ))}
            <div className="score-total">
              <span>Total Score</span>
              <span className="score-total__val">{totalScore} / 100</span>
            </div>
            <button className="btn btn--primary btn--full" style={{ marginTop: 12 }} onClick={saveScore} disabled={savingScore}>
              {savingScore ? 'Saving...' : 'Save Score'}
            </button>
          </div>
        </div>

        {/* Right column — application content */}
        <div className="detail-right">
          <div className="card mb-2">
            <h3 className="card-title">Business Challenge</h3>
            <DetailRow label="Problem Statement" value={app.manual_process_problem} />
            <DetailRow label="Current Process" value={app.current_process} />
            <DetailRow label="Time per Week" value={app.weekly_time_spent} />
            <DetailRow label="Monthly Loss (LKR)" value={app.estimated_monthly_loss} />
            <DetailRow label="Current Tools" value={app.current_tools} />
          </div>

          <div className="card mb-2">
            <h3 className="card-title">Expected Impact</h3>
            <DetailRow label="Automation Impact" value={app.expected_automation_impact} />
            <DetailRow label="Measurable Improvements" value={app.measurable_improvements} />
          </div>

          <div className="card mb-2">
            <h3 className="card-title">Selection Justification</h3>
            <DetailRow label="Why Selected" value={app.selection_reason} />
            <DetailRow label="Broader Impact" value={app.expected_business_impact} />
          </div>

          {/* Comments */}
          <div className="card">
            <h3 className="card-title">Internal Comments</h3>
            {comments.length === 0 && <p style={{ fontSize: 13, color: 'var(--text3)' }}>No comments yet.</p>}
            {comments.map(c => (
              <div key={c.id} className="comment">
                <div className="comment__meta">{c.admin_name} · {new Date(c.created_at).toLocaleString('en-LK')}</div>
                <div className="comment__text">{c.comment}</div>
              </div>
            ))}
            <div className="comment-input">
              <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Add an internal note..." style={{ minHeight: 72 }} />
              <button className="btn btn--primary" style={{ alignSelf: 'flex-end' }} onClick={addComment} disabled={savingComment || !comment.trim()}>
                {savingComment ? '...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
